import React, { useState, useEffect, useCallback } from 'react';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import { useNavigate, Link } from 'react-router-dom';
import BridgeClubLayout from '../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import { useLocalAccess } from '../../hooks/useLocalAccess';
import './BulkPreOrder.css';

interface BulkFeedCategory {
  value: string;
  label: string;
}

interface BulkFeed {
  id: number;
  code: string;
  name: string;
  description: string;
  animal_type: string;
  animal_type_display: string;
  feed_grade: string;
  feed_grade_display: string;
  target_animal: string;
  available_forms: string[];
  pellets_available: boolean;
  price_1000lb_mash: string | null;
  price_1000lb_pellet: string | null;
  price_2000lb_mash: string | null;
  price_2000lb_pellet: string | null;
  price_per_lb_1000_mash: string | null;
  price_per_lb_1000_pellet: string | null;
  price_per_lb_2000_mash: string | null;
  price_per_lb_2000_pellet: string | null;
}

interface OrderItem {
  formula: BulkFeed;
  quantity_lbs: number;
  form: 'MASH' | 'PELLET';
}

interface OrderRequirements {
  min_pickup_date: string;
  lead_time_days: number;
  payment_methods: Array<{
    value: string;
    label: string;
    description: string;
    fee_percent: number;
    fee_fixed: number;
  }>;
  tax_rate: number;
}

const BulkPreOrder: React.FC = () => {
  const navigate = useNavigate();
  const { hasLocalAccess } = useLocalAccess();

  // State
  const [categories, setCategories] = useState<BulkFeedCategory[]>([]);
  const [feeds, setFeeds] = useState<BulkFeed[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  // Server search: input binds to `searchInput` (instant); debounced `searchQuery` (400ms)
  // drives loadFeeds so typing fires one request after the pause.
  const { value: searchInput, setValue: setSearchInput, debounced: searchQuery } = useDebouncedSearch('', 400);
  const [loading, setLoading] = useState(true);

  // Order building state
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedFeed, setSelectedFeed] = useState<BulkFeed | null>(null);
  const [selectedForm, setSelectedForm] = useState<'MASH' | 'PELLET'>('MASH');
  const [selectedQuantity, setSelectedQuantity] = useState<1000 | 2000>(1000);

  // Checkout state
  const [showCheckout, setShowCheckout] = useState(false);
  const [requirements, setRequirements] = useState<OrderRequirements | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'ach' | 'card'>('ach');
  const [pickupDate, setPickupDate] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [pendingOrderNumber, setPendingOrderNumber] = useState<string | null>(null);

  // Listen for Helcim Pay modal completion
  const handleHelcimMessage = useCallback((event: MessageEvent) => {
    if (!event.origin.includes('helcim.com')) return;
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    if (data.eventName === 'helcim-pay-success' || data.eventStatus === 'APPROVED') {
      if (pendingOrderNumber) {
        navigate(`/bridge-club/bulk-orders/${pendingOrderNumber}`);
      }
    } else if (data.eventName === 'helcim-pay-error' || data.eventStatus === 'DECLINED' || data.eventStatus === 'ERROR') {
      setError(data.error || data.message || 'Payment was declined. Please try again.');
    } else if (data.eventName === 'helcim-pay-close') {
      if (pendingOrderNumber) {
        navigate(`/bridge-club/bulk-orders/${pendingOrderNumber}`);
      }
    }
  }, [pendingOrderNumber, navigate]);

  useEffect(() => {
    window.addEventListener('message', handleHelcimMessage);
    return () => window.removeEventListener('message', handleHelcimMessage);
  }, [handleHelcimMessage]);

  useEffect(() => {
    if (!hasLocalAccess) {
      navigate('/bridge-club');
      return;
    }
    loadCategories();
    loadFeeds();
    loadRequirements();
  }, [hasLocalAccess, navigate]);

  useEffect(() => {
    if (hasLocalAccess) {
      loadFeeds();
    }
  }, [selectedCategory, searchQuery, hasLocalAccess]);

  const loadCategories = async () => {
    try {
      const data = await bridgeClubAPI.getBulkFeedCategories();
      setCategories(data || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadFeeds = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getBulkFeeds({
        animal_type: selectedCategory || undefined,
        search: searchQuery || undefined,
      });
      setFeeds(data || []);
    } catch (err) {
      console.error('Failed to load feeds:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRequirements = async () => {
    try {
      const data = await bridgeClubAPI.getBulkOrderRequirements();
      setRequirements(data);
      if (data.min_pickup_date) {
        setPickupDate(data.min_pickup_date);
      }
    } catch (err) {
      console.error('Failed to load requirements:', err);
    }
  };

  const getPrice = (feed: BulkFeed, form: 'MASH' | 'PELLET', quantity: number) => {
    if (quantity >= 2000) {
      return form === 'PELLET' ? feed.price_2000lb_pellet : feed.price_2000lb_mash;
    }
    return form === 'PELLET' ? feed.price_1000lb_pellet : feed.price_1000lb_mash;
  };

  const addToOrder = () => {
    if (!selectedFeed) return;

    const price = getPrice(selectedFeed, selectedForm, selectedQuantity);
    if (!price) {
      setError(`${selectedForm} form not available at ${selectedQuantity} lbs`);
      return;
    }

    setOrderItems([
      ...orderItems,
      {
        formula: selectedFeed,
        quantity_lbs: selectedQuantity,
        form: selectedForm,
      },
    ]);
    setSelectedFeed(null);
    setError('');
  };

  const removeFromOrder = (index: number) => {
    setOrderItems(orderItems.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return orderItems.reduce((total, item) => {
      const price = getPrice(item.formula, item.form, item.quantity_lbs);
      if (!price) return total;

      // Price is per tier (1000 or 2000), calculate proportionally
      const tierSize = item.quantity_lbs >= 2000 ? 2000 : 1000;
      const pricePerLb = parseFloat(price) / tierSize;
      return total + pricePerLb * item.quantity_lbs;
    }, 0);
  };

  const calculateFee = () => {
    if (paymentMethod !== 'card') return 0;
    const subtotal = calculateSubtotal();
    return subtotal * 0.029 + 0.30;
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const fee = calculateFee();
    return (subtotal + fee) * 0.08;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateFee() + calculateTax();
  };

  const handleSubmitOrder = async () => {
    if (orderItems.length === 0) {
      setError('Please add at least one item to your order');
      return;
    }

    if (!pickupDate) {
      setError('Please select a pickup date');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const result = await bridgeClubAPI.createBulkOrder({
        items: orderItems.map((item) => ({
          formula_id: item.formula.id,
          quantity_lbs: item.quantity_lbs,
          form: item.form,
        })),
        payment_method: paymentMethod,
        requested_pickup_date: pickupDate,
        notes,
      });

      // If checkout token available, show embedded Helcim modal
      if (result.checkout_token && window.appendHelcimPayIframe) {
        setPendingOrderNumber(result.order_number);
        window.appendHelcimPayIframe(result.checkout_token);
      } else if (result.payment_link) {
        // Fallback: navigate to order detail
        navigate(`/bridge-club/bulk-orders/${result.order_number}`);
      } else {
        // ACH/cash/invoice - navigate to order detail
        navigate(`/bridge-club/bulk-orders/${result.order_number}`);
      }
    } catch (err: any) {
      console.error('Failed to create order:', err);
      setError(err.error || 'Failed to create order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasLocalAccess) {
    return null;
  }

  return (
    <BridgeClubLayout>
      <div className="bulk-preorder">
        <div className="page-header">
          <Link to="/bridge-club/shop" className="back-link">&larr; Back to Shop</Link>
          <h1>Bulk Pre-Order</h1>
          <p className="subtitle">Order custom feed in 1000lb or 2000lb quantities</p>
        </div>

        {!showCheckout ? (
          <>
            {/* Feed Selection */}
            <div className="feed-selection">
              <div className="filters">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-select"
                >
                  <option value="">All Animal Types</option>
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Search feeds..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="search-input"
                />
              </div>

              {loading && feeds.length === 0 ? (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Loading feeds...</p>
                </div>
              ) : feeds.length === 0 ? (
                <div className="empty">
                  <p>No bulk feeds available</p>
                </div>
              ) : (
                <div className="feeds-grid">
                  {feeds.map((feed) => (
                    <div
                      key={feed.id}
                      className={`feed-card ${selectedFeed?.id === feed.id ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedFeed(feed);
                        // Default to first available form
                        if (feed.available_forms.length > 0) {
                          setSelectedForm(feed.available_forms[0] as 'MASH' | 'PELLET');
                        }
                      }}
                    >
                      <div className="feed-header">
                        <span className="feed-grade">{feed.feed_grade_display}</span>
                        <span className="animal-type">{feed.animal_type_display}</span>
                      </div>
                      <h3 className="feed-name">{feed.name}</h3>
                      <p className="feed-target">{feed.target_animal}</p>
                      <div className="feed-pricing">
                        {feed.price_1000lb_mash && (
                          <div className="price-tier">
                            <span className="tier">1000 lbs:</span>
                            <span className="price">${parseFloat(feed.price_1000lb_mash).toFixed(2)}</span>
                          </div>
                        )}
                        {feed.price_2000lb_mash && (
                          <div className="price-tier">
                            <span className="tier">2000 lbs:</span>
                            <span className="price">${parseFloat(feed.price_2000lb_mash).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <div className="available-forms">
                        {feed.available_forms.map((form) => (
                          <span key={form} className="form-tag">
                            {form}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add to Order Panel */}
            {selectedFeed && (
              <div className="add-to-order-panel">
                <h3>Add to Order: {selectedFeed.name}</h3>
                <div className="options">
                  <div className="option-group">
                    <label>Form</label>
                    <div className="option-buttons">
                      {selectedFeed.available_forms.map((form) => (
                        <button
                          key={form}
                          className={`option-btn ${selectedForm === form ? 'active' : ''}`}
                          onClick={() => setSelectedForm(form as 'MASH' | 'PELLET')}
                        >
                          {form}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="option-group">
                    <label>Quantity</label>
                    <div className="option-buttons">
                      <button
                        className={`option-btn ${selectedQuantity === 1000 ? 'active' : ''}`}
                        onClick={() => setSelectedQuantity(1000)}
                        disabled={!getPrice(selectedFeed, selectedForm, 1000)}
                      >
                        1000 lbs
                        {getPrice(selectedFeed, selectedForm, 1000) && (
                          <span className="btn-price">
                            ${parseFloat(getPrice(selectedFeed, selectedForm, 1000)!).toFixed(2)}
                          </span>
                        )}
                      </button>
                      <button
                        className={`option-btn ${selectedQuantity === 2000 ? 'active' : ''}`}
                        onClick={() => setSelectedQuantity(2000)}
                        disabled={!getPrice(selectedFeed, selectedForm, 2000)}
                      >
                        2000 lbs
                        {getPrice(selectedFeed, selectedForm, 2000) && (
                          <span className="btn-price">
                            ${parseFloat(getPrice(selectedFeed, selectedForm, 2000)!).toFixed(2)}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                <button className="add-btn" onClick={addToOrder}>
                  Add to Order
                </button>
              </div>
            )}

            {/* Order Summary */}
            {orderItems.length > 0 && (
              <div className="order-summary">
                <h3>Your Order ({orderItems.length} items)</h3>
                <div className="order-items">
                  {orderItems.map((item, index) => {
                    const price = getPrice(item.formula, item.form, item.quantity_lbs);
                    const tierSize = item.quantity_lbs >= 2000 ? 2000 : 1000;
                    const pricePerLb = price ? parseFloat(price) / tierSize : 0;
                    const lineTotal = pricePerLb * item.quantity_lbs;

                    return (
                      <div key={index} className="order-item">
                        <div className="item-info">
                          <span className="item-name">{item.formula.name}</span>
                          <span className="item-details">
                            {item.quantity_lbs} lbs - {item.form}
                          </span>
                        </div>
                        <div className="item-price">
                          <span>${lineTotal.toFixed(2)}</span>
                          <button
                            className="remove-btn"
                            onClick={() => removeFromOrder(index)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="order-totals">
                  <div className="total-row">
                    <span>Subtotal</span>
                    <span>${calculateSubtotal().toFixed(2)}</span>
                  </div>
                </div>
                <button
                  className="proceed-btn"
                  onClick={() => setShowCheckout(true)}
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </>
        ) : (
          /* Checkout View */
          <div className="checkout-view">
            <button className="back-btn" onClick={() => setShowCheckout(false)}>
              Back to Order
            </button>

            <div className="checkout-section">
              <h3>Order Items</h3>
              <div className="checkout-items">
                {orderItems.map((item, index) => {
                  const price = getPrice(item.formula, item.form, item.quantity_lbs);
                  const tierSize = item.quantity_lbs >= 2000 ? 2000 : 1000;
                  const pricePerLb = price ? parseFloat(price) / tierSize : 0;
                  const lineTotal = pricePerLb * item.quantity_lbs;

                  return (
                    <div key={index} className="checkout-item">
                      <span>{item.formula.name}</span>
                      <span>
                        {item.quantity_lbs} lbs {item.form}
                      </span>
                      <span>${lineTotal.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="checkout-section">
              <h3>Pickup Date</h3>
              <p className="section-note">
                Bulk orders require a 7-day lead time for milling.
              </p>
              <input
                type="date"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                min={requirements?.min_pickup_date || ''}
                className="date-input"
              />
            </div>

            <div className="checkout-section">
              <h3>Payment Method</h3>
              <div className="payment-options">
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="ach"
                    checked={paymentMethod === 'ach'}
                    onChange={() => setPaymentMethod('ach')}
                  />
                  <div className="payment-info">
                    <span className="payment-label">Bank Transfer (ACH) - No fee</span>
                    <span className="payment-desc">Your order will be ready in ~7 days</span>
                  </div>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={() => setPaymentMethod('card')}
                  />
                  <div className="payment-info">
                    <span className="payment-label">Credit/Debit Card</span>
                    <span className="payment-desc">+ ${calculateFee().toFixed(2)} processing fee</span>
                  </div>
                </label>
              </div>

              {/* ACH Savings Callout */}
              {paymentMethod === 'card' && calculateFee() > 0 && (
                <div className="savings-callout">
                  <span className="savings-icon">💡</span>
                  <div className="savings-text">
                    <strong>Save ${calculateFee().toFixed(2)} with bank transfer!</strong>
                    <span>Same 7-day lead time either way - your feed is milled fresh to order.</span>
                  </div>
                </div>
              )}
            </div>

            <div className="checkout-section">
              <h3>Order Notes (Optional)</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions for your order..."
                rows={3}
              />
            </div>

            <div className="checkout-totals">
              <div className="total-row">
                <span>Subtotal</span>
                <span>${calculateSubtotal().toFixed(2)}</span>
              </div>
              {paymentMethod === 'card' && (
                <div className="total-row">
                  <span>Card Processing Fee</span>
                  <span>${calculateFee().toFixed(2)}</span>
                </div>
              )}
              <div className="total-row">
                <span>Tax (8%)</span>
                <span>${calculateTax().toFixed(2)}</span>
              </div>
              <div className="total-row total">
                <span>Total</span>
                <span>${calculateTotal().toFixed(2)}</span>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              className="submit-btn"
              onClick={handleSubmitOrder}
              disabled={submitting || orderItems.length === 0}
            >
              {submitting
                ? 'Processing...'
                : paymentMethod === 'card'
                ? 'Continue to Payment'
                : 'Place Order'}
            </button>
          </div>
        )}
      </div>
    </BridgeClubLayout>
  );
};

export default BulkPreOrder;
